/**
 * Media picker — the library grid and an upload dropzone in one dialog.
 *
 * Selection is staged locally so the user can pick several assets (or upload
 * new ones, which auto-select) and commit once.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Clapperboard, ImagePlus, Search, Sparkles } from 'lucide-react';
import type { MediaAsset } from '../../../shared/types';
import { formatBytes } from '../../../shared/utils';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Input,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui';
import { UploadDropzone } from '../media/UploadDropzone';
import { api } from '../../lib/api';
import { qk } from '../../lib/query';
import { cn, debounce } from '../../lib/utils';
import { useWorkspaceStore } from '../../stores/workspace';
import { assetUrl } from './composerUtils';

export interface MediaPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Assets already attached — pre-checked when the dialog opens. */
  selected: MediaAsset[];
  onConfirm: (assets: MediaAsset[]) => void;
  title?: string;
  description?: string;
}

export function MediaPickerDialog({
  open,
  onOpenChange,
  selected,
  onConfirm,
  title = 'Add media',
  description = 'Pick from the library or upload something new.',
}: MediaPickerDialogProps) {
  const workspaceId = useWorkspaceStore((state) => state.workspaceId);
  const queryClient = useQueryClient();

  const [tab, setTab] = useState('library');
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [selection, setSelection] = useState<string[]>(() => selected.map((asset) => asset.id));
  const [uploaded, setUploaded] = useState<MediaAsset[]>([]);

  const selectedIds = useMemo(() => selected.map((asset) => asset.id), [selected]);
  const commitSearch = useMemo(() => debounce((value: string) => setSearch(value), 300), []);

  useEffect(() => {
    if (!open) return;
    setSelection(selectedIds);
    setUploaded([]);
    setTab('library');
    setSearchText('');
    setSearch('');
    // Re-seeding on every open is the point — the caller owns the truth.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filters = { search: search || undefined };
  const query = useQuery({
    queryKey: qk.mediaList(workspaceId, filters),
    queryFn: () => api.media.list({ ...filters, limit: 60 }),
    enabled: open && Boolean(workspaceId),
    placeholderData: (previous) => previous,
  });

  const assets = useMemo(() => {
    const fromServer = query.data?.assets ?? [];
    const seen = new Set(fromServer.map((asset) => asset.id));
    return [...uploaded.filter((asset) => !seen.has(asset.id)), ...fromServer];
  }, [query.data, uploaded]);

  /** Every asset we can resolve an id back to, including ones off this page. */
  const known = useMemo(() => {
    const map = new Map<string, MediaAsset>();
    for (const asset of [...selected, ...uploaded, ...assets]) map.set(asset.id, asset);
    return map;
  }, [selected, uploaded, assets]);

  const toggle = (assetId: string) => {
    setSelection((current) =>
      current.includes(assetId)
        ? current.filter((id) => id !== assetId)
        : [...current, assetId],
    );
  };

  const onUploaded = (asset: MediaAsset) => {
    setUploaded((current) => [asset, ...current]);
    setSelection((current) => (current.includes(asset.id) ? current : [...current, asset.id]));
    void queryClient.invalidateQueries({ queryKey: ['ws', workspaceId, 'media'] });
  };

  const added = selection.filter((id) => !selectedIds.includes(id)).length;
  const removed = selectedIds.filter((id) => !selection.includes(id)).length;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      size="xl"
      bodyClassName="px-5 py-4"
      footer={
        <>
          <p className="mr-auto text-xs text-subtle">
            {selection.length} selected
            {added > 0 || removed > 0 ? ` · ${added} added, ${removed} removed` : ''}
          </p>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onConfirm(
                selection
                  .map((id) => known.get(id))
                  .filter((asset): asset is MediaAsset => Boolean(asset)),
              );
              onOpenChange(false);
            }}
          >
            Use selection
          </Button>
        </>
      }
    >
      <Tabs value={tab} onValueChange={setTab} variant="pill">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TabsList>
            <TabsTrigger value="library">Library</TabsTrigger>
            <TabsTrigger value="upload">Upload</TabsTrigger>
          </TabsList>
          {tab === 'library' && (
            <Input
              value={searchText}
              onChange={(event) => {
                setSearchText(event.target.value);
                commitSearch(event.target.value);
              }}
              inputSize="sm"
              placeholder="Search filenames and alt text"
              leadingIcon={<Search />}
              wrapperClassName="w-full sm:w-64"
              aria-label="Search media"
            />
          )}
        </div>

        <TabsContent value="library">
          {query.isPending ? (
            <div className="grid max-h-[26rem] grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-3 lg:grid-cols-4">
              {Array.from({ length: 8 }).map((_, index) => (
                <Skeleton key={index} className="aspect-square rounded-lg" />
              ))}
            </div>
          ) : assets.length === 0 ? (
            <EmptyState
              icon={<ImagePlus className="size-5" />}
              title={search ? 'Nothing matches that search' : 'Your library is empty'}
              description={
                search
                  ? 'Try a different filename, or upload something new.'
                  : 'Upload an image or video and it will be available to every post.'
              }
              action={
                <Button variant="secondary" onClick={() => setTab('upload')}>
                  Upload media
                </Button>
              }
            />
          ) : (
            <ul className="grid max-h-[26rem] grid-cols-2 gap-3 overflow-y-auto pr-1 sm:grid-cols-3 lg:grid-cols-4">
              {assets.map((asset) => {
                const checked = selection.includes(asset.id);
                return (
                  <li key={asset.id}>
                    <button
                      type="button"
                      onClick={() => toggle(asset.id)}
                      aria-pressed={checked}
                      className={cn(
                        'group w-full overflow-hidden rounded-lg border text-left transition-[border-color,box-shadow]',
                        checked
                          ? 'border-accent ring-2 ring-accent/25'
                          : 'border-border hover:border-border-strong',
                      )}
                    >
                      <div className="relative aspect-square bg-sunken">
                        {asset.kind === 'image' ? (
                          <img
                            src={assetUrl(asset)}
                            alt={asset.altText ?? asset.filename}
                            loading="lazy"
                            className="size-full object-cover"
                          />
                        ) : (
                          <div className="flex size-full items-center justify-center text-subtle">
                            <Clapperboard className="size-6" aria-hidden />
                          </div>
                        )}
                        <span
                          className={cn(
                            'absolute right-2 top-2 flex size-5 items-center justify-center rounded-full border transition-colors',
                            checked
                              ? 'border-accent bg-accent text-accent-fg'
                              : 'border-white/70 bg-black/35 text-transparent',
                          )}
                          aria-hidden
                        >
                          <Check className="size-3" />
                        </span>
                        {asset.source === 'ai_generated' && (
                          <span className="absolute left-2 top-2 flex size-5 items-center justify-center rounded-full bg-black/55 text-white">
                            <Sparkles className="size-3" aria-hidden />
                          </span>
                        )}
                        {!asset.altText && asset.kind === 'image' && (
                          <span className="absolute bottom-2 left-2">
                            <Badge tone="warning" size="sm">
                              No alt text
                            </Badge>
                          </span>
                        )}
                      </div>
                      <div className="space-y-0.5 px-2.5 py-2">
                        <p className="truncate text-xs font-medium text-fg">{asset.filename}</p>
                        <p className="text-[11px] text-subtle">
                          {formatBytes(asset.size)}
                          {asset.width && asset.height ? ` · ${asset.width}×${asset.height}` : ''}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </TabsContent>

        <TabsContent value="upload">
          <UploadDropzone onUploaded={onUploaded} />
          {uploaded.length > 0 && (
            <p className="mt-3 text-xs text-muted">
              {uploaded.length} upload{uploaded.length === 1 ? '' : 's'} added to this post.
            </p>
          )}
        </TabsContent>
      </Tabs>
    </Dialog>
  );
}
